import { useWorkspaceAsset } from '@gripp/shared-logic';
import {
  ColumnData,
  RoutineLabelPickerPanel,
  RoutineLabelPickerProvider,
  RoutineLabelPickerRequest,
  SlideAction,
  SlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import React, { useRef, useState } from 'react';
import { View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { RoutineSubmitSource } from '../assetUtils';
import { AssetRoutineSubmitColumn } from './asset-routine-submit-column';
import { AssetRoutineSubmitHeader } from './asset-routine-submit-header';
import { RoutineAllColumn } from './asset-routines-all-column';
import { AssetRoutinesHeader } from './asset-routines-header';
import { AssetCard } from './assetCard';

export const AssetRoutineSubmit = () => {
  const { assetId, routineId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backGuardRef = useRef<((onProceed: () => void, onCancel?: () => void) => void) | null>(null);
  const [labelPickerRequest, setLabelPickerRequest] =
    useState<RoutineLabelPickerRequest | null>(null);
  const { workspaceAsset } = useWorkspaceAsset(assetId ?? '');

  if (!assetId || !routineId) {
    return null;
  }

  const from: RoutineSubmitSource = location.state?.from ?? RoutineSubmitSource.AssetCard;
  const cameFromRoutinesList = from === RoutineSubmitSource.RoutinesList;

  const backButtonGuard = (): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      if (!backGuardRef.current) {
        resolve(true);
        return;
      }
      backGuardRef.current(
        () => resolve(true),
        () => resolve(false)
      );
    });
  };

  const leftColumn = {
    title: 'assets.routines',
    mainComponent: (
      cameFromRoutinesList ? (
        <RoutineAllColumn assetId={assetId} routineId={routineId} />
      ) : (
        <AssetCard assetId={assetId} />
      )
    ),
    headerComponent: (
      <AssetRoutinesHeader assetId={assetId} />
    ),
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonGuard,
    onBackButtonPress: () => {
      navigate(`/assets/${assetId}`);
    }
  };

  const rightColumn: ColumnData = {
    title: 'routines.editRoutines.editRoutinesButton',
    mainComponent: (
      <RoutineLabelPickerProvider onRequestChange={setLabelPickerRequest}>
        <AssetRoutineSubmitColumn
          assetId={assetId}
          backGuardRef={backGuardRef}
        />
      </RoutineLabelPickerProvider>
    ),
    showBackButton: true,
    disableScroll: true,
    headerComponent: (
      <AssetRoutineSubmitHeader
        assetId={assetId}
        routineId={routineId}
      />
    ),
    backButtonTransition: SlideAction.PREV,
    backButtonGuard,
    onBackButtonPress: () => {
      const url = cameFromRoutinesList ?
        `/assets/${assetId}/routines/all` :
        `/assets/${assetId}`;
      navigate(url);
    }
  };

  const thirdColumn: ColumnData = {
    title: 'activity.filter.labels',
    mainComponent: (
      <LabelPickerThirdColumnContent
        request={labelPickerRequest}
        workspaceId={workspaceAsset?.workspace?.id}
      />
    ),
    showBackButton: true,
    disableScroll: true,
  };

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      thirdColumn={thirdColumn}
      viewMode={ViewMode.DUAL}
    />
  );
};

const LabelPickerThirdColumnContent = ({
  request,
  workspaceId,
}: {
  request: RoutineLabelPickerRequest | null;
  workspaceId?: string;
}) => {
  if (!request || !workspaceId) {
    return <View />;
  }

  return (
    <RoutineLabelPickerPanel
      workspaceId={workspaceId}
      initialItems={request.initialItems}
      onChange={request.onChange}
    />
  );
};
